import { Activity, useContext, useLayoutEffect, useState } from 'react'
import { useLocation, useNavigationType, UNSAFE_LocationContext, UNSAFE_ViewTransitionContext, type Location, type NavigationType } from 'react-router-dom'
import { TabActiveContext, TabVisitContext } from '@/hooks/useTabVisit'
import { activeTabPath, type KeptTab } from '@/lib/keptTabs'

interface Shown {
  // Kept as one object so a hidden tab's context value stays referentially stable.
  context: { location: Location; navigationType: NavigationType }
  visit: number
}

// Tab roots stay mounted once visited and are hidden with <Activity> while
// another route shows, so switching tabs reveals the page instead of mounting
// it again. Each tab reads the location it was last shown at: a hidden tab still
// re-renders on context changes, and another route's URL (its search params, its
// history state) must not leak into it.
export function TabKeepAlive({ tabs }: { tabs: readonly KeptTab[] }) {
  const location = useLocation()
  const navigationType = useNavigationType()
  const active = activeTabPath(tabs, location.pathname)
  const [state, setState] = useState<{ current: string | undefined; leaving: string | undefined; shown: Record<string, Shown> }>({ current: undefined, leaving: undefined, shown: {} })

  if (active !== state.current || (active && state.shown[active].context.location !== location)) {
    const prev = active ? state.shown[active] : undefined
    setState({
      current: active,
      leaving: active !== state.current ? state.current : state.leaving,
      shown: active
        ? { ...state.shown, [active]: { context: { location, navigationType }, visit: (prev?.visit ?? 0) + (active === state.current ? 0 : 1) } }
        : state.shown,
    })
  }

  // react-router pushes the entry and publishes the destination here synchronously,
  // but renders it inside startViewTransition's callback, after the old-state
  // snapshot — a frame or more later. The tab being left drops its floaters now.
  const vt = useContext(UNSAFE_ViewTransitionContext)
  const departing = vt.isTransitioning && activeTabPath(tabs, vt.nextLocation.pathname) !== state.current ? state.current : undefined

  // A hidden tab only re-renders at idle priority, which can be seconds away on
  // a phone, so the tab being left stays visible for one commit with
  // TabActiveContext already false: its floaters (portaled out of it, where
  // <Activity> can't hide them) unmount in that commit. It hides here, before
  // the browser paints the two tabs together.
  useLayoutEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect -- the synchronous second commit is the point
    if (state.leaving) setState(s => ({ ...s, leaving: undefined }))
  }, [state.leaving])

  return tabs.map(tab => {
    const shown = state.shown[tab.path]
    if (!shown) return null
    const visible = tab.path === state.current
    return (
      <Activity key={tab.path} mode={visible || tab.path === state.leaving ? 'visible' : 'hidden'}>
        <UNSAFE_LocationContext.Provider value={shown.context}>
          <TabVisitContext.Provider value={shown.visit}>
            <TabActiveContext.Provider value={visible && tab.path !== departing}>{tab.element}</TabActiveContext.Provider>
          </TabVisitContext.Provider>
        </UNSAFE_LocationContext.Provider>
      </Activity>
    )
  })
}
